import { useEffect, useState, type RefObject } from 'react';

export interface ScrollEdges {
  /** Content is hidden past the left edge. */
  start: boolean;
  /** Content is hidden past the right edge. */
  end: boolean;
}

const NONE: ScrollEdges = { start: false, end: false };

/*
 * Which sides of a sideways-scrolling box still hide content. Rounding leaves
 * a fraction of a pixel at the far end, so one pixel counts as none.
 */
function readEdges(box: HTMLElement): ScrollEdges {
  const hidden = box.scrollWidth - box.clientWidth;

  return {
    start: box.scrollLeft > 1,
    end: box.scrollLeft < hidden - 1,
  };
}

export function useScrollEdges(ref: RefObject<HTMLElement | null>): ScrollEdges {
  const [edges, setEdges] = useState<ScrollEdges>(NONE);

  useEffect(() => {
    const box = ref.current;
    if (!box) {
      return;
    }

    function update() {
      const next = readEdges(box!);
      setEdges((current) =>
        current.start === next.start && current.end === next.end ? current : next,
      );
    }

    update();
    box.addEventListener('scroll', update, { passive: true });
    /* The box narrows with the window; its content widens as rows arrive. */
    const observer = new ResizeObserver(update);
    observer.observe(box);
    for (const child of box.children) {
      observer.observe(child);
    }

    return () => {
      box.removeEventListener('scroll', update);
      observer.disconnect();
    };
  }, [ref]);

  return edges;
}
